import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';

const DiscountContext = createContext();

export const useDiscounts = () => {
  const context = useContext(DiscountContext);
  if (!context) {
    throw new Error('useDiscounts must be used within a DiscountProvider');
  }
  return context;
};

export const DiscountProvider = ({ children }) => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load discounts from Firestore
  const loadDiscounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const discountsRef = collection(db, 'discounts');
      const q = query(
        discountsRef,
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const discountsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        discountsData.push({
          id: doc.id,
          ...data,
          // Convert Firestore timestamps to Date objects
          startDate: data.startDate ? new Date(data.startDate + 'T00:00:00') : null,
          endDate: data.endDate ? new Date(data.endDate + 'T23:59:59') : null,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        });
      });
      
      setDiscounts(discountsData);
    } catch (err) {
      console.error('Error loading discounts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Set up real-time listener for discounts
  useEffect(() => {
    const discountsRef = collection(db, 'discounts');
    
    const q = query(
      discountsRef,
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const discountsData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        discountsData.push({
          id: doc.id,
          ...data,
          startDate: data.startDate ? new Date(data.startDate + 'T00:00:00') : null,
          endDate: data.endDate ? new Date(data.endDate + 'T23:59:59') : null,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt
        });
      });
      
      setDiscounts(discountsData);
      setLoading(false);
    }, (err) => {
      console.error('Error in discount listener:', err);
      setError(err.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Check if a discount is currently valid
  const isDiscountValid = useCallback((discount) => {
    if (!discount.isActive) {
      return false;
    }
    
    const now = new Date();
    
    // Check start date
    if (discount.startDate) {
      const startDate = new Date(discount.startDate);
      if (now < startDate) {
        return false;
      }
    }
    
    // Check end date
    if (discount.endDate) {
      const endDate = new Date(discount.endDate);
      if (now > endDate) {
        return false;
      }
    }
    
    return true;
  }, []);

  // Get applicable discounts for a product
  const getProductDiscounts = useCallback((productId, categoryId, orderAmount = 0) => {
    const validDiscounts = discounts.filter(discount => isDiscountValid(discount));
    
    // Sort by priority: product > category > global
    const productDiscounts = validDiscounts.filter(d => d.scope === 'product' && d.targetId === productId);
    
    const categoryDiscounts = validDiscounts.filter(d => d.scope === 'category' && d.targetId === categoryId);
    
    const globalDiscounts = validDiscounts.filter(d => d.scope === 'global');
    
    // Filter by order amount requirements
    const applicableDiscounts = [];
    
    // Add product-level discounts
    productDiscounts.forEach(discount => {
      if (orderAmount >= (discount.minOrderAmount || 0)) {
        applicableDiscounts.push({ ...discount, priority: 1 });
      }
    });
    
    // Add category-level discounts (only if no product discount exists)
    if (productDiscounts.length === 0) {
      categoryDiscounts.forEach(discount => {
        if (orderAmount >= (discount.minOrderAmount || 0)) {
          applicableDiscounts.push({ ...discount, priority: 2 });
        }
      });
    }
    
    // Add global discounts (only if no product or category discount exists)
    if (productDiscounts.length === 0 && categoryDiscounts.length === 0) {
      globalDiscounts.forEach(discount => {
        if (orderAmount >= (discount.minOrderAmount || 0)) {
          applicableDiscounts.push({ ...discount, priority: 3 });
        }
      });
    }
    
    // Return the highest priority discount
    return applicableDiscounts.sort((a, b) => a.priority - b.priority);
  }, [discounts, isDiscountValid]);

  // Calculate discount amount for a product
  const calculateProductDiscount = useCallback((productId, categoryId, originalPrice, orderAmount = 0) => {
    const applicableDiscounts = getProductDiscounts(productId, categoryId, orderAmount);
    
    if (applicableDiscounts.length === 0) {
      return { discountAmount: 0, finalPrice: originalPrice, discount: null };
    }
    
    const bestDiscount = applicableDiscounts[0];
    let discountAmount = 0;
    
    if (bestDiscount.type === 'percentage') {
      discountAmount = (originalPrice * bestDiscount.value) / 100;
      // Apply max discount limit if specified
      if (bestDiscount.maxDiscount && discountAmount > bestDiscount.maxDiscount) {
        discountAmount = bestDiscount.maxDiscount;
      }
    } else if (bestDiscount.type === 'fixed_amount') {
      discountAmount = bestDiscount.value;
      // Ensure discount doesn't exceed the product price
      if (discountAmount > originalPrice) {
        discountAmount = originalPrice;
      }
    }
    
    const finalPrice = Math.max(0, originalPrice - discountAmount);
    
    const result = {
      discountAmount: Math.round(discountAmount), // Round to whole number
      finalPrice: Math.round(finalPrice), // Round to whole number
      discount: bestDiscount
    };
    
    return result;
  }, [getProductDiscounts, discounts]);

  // Get all active discounts for display
  const getActiveDiscounts = useCallback(() => {
    return discounts.filter(discount => isDiscountValid(discount));
  }, [discounts, isDiscountValid]);

  // Check if a discount is expiring soon (within 7 days)
  const isExpiringSoon = useCallback((discount) => {
    if (!discount.endDate) return false;
    
    const now = new Date();
    const endDate = new Date(discount.endDate);
    const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  }, []);

  // Check if a discount has expired
  const isExpired = useCallback((discount) => {
    if (!discount.endDate) return false;
    
    const now = new Date();
    const endDate = new Date(discount.endDate);
    return now > endDate;
  }, []);

  // Get days until discount expires
  const getDaysUntilExpiry = useCallback((discount) => {
    if (!discount.endDate) return null;
    
    const now = new Date();
    const endDate = new Date(discount.endDate);
    const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    
    return daysUntilExpiry;
  }, []);

  // Get discount status for admin purposes
  const getDiscountStatus = useCallback((discount) => {
    if (!discount.isActive) return 'inactive';
    if (isExpired(discount)) return 'expired';
    if (isExpiringSoon(discount)) return 'expiring_soon';
    return 'active';
  }, [isExpired, isExpiringSoon]);

  // Get expired discounts for admin cleanup
  const getExpiredDiscounts = useCallback(() => {
    return discounts.filter(discount => isExpired(discount) && discount.isActive);
  }, [discounts, isExpired]);

  // Get discounts that need attention (expiring soon or expired)
  const getDiscountsNeedingAttention = useCallback(() => {
    return discounts.filter(discount => 
      discount.isActive && (isExpiringSoon(discount) || isExpired(discount))
    );
  }, [discounts, isExpiringSoon, isExpired]);

  const value = {
    discounts,
    loading,
    error,
    loadDiscounts,
    getProductDiscounts,
    calculateProductDiscount,
    getActiveDiscounts,
    isDiscountValid,
    isExpiringSoon,
    isExpired,
    getDaysUntilExpiry,
    getDiscountStatus,
    getExpiredDiscounts,
    getDiscountsNeedingAttention
  };

  return (
    <DiscountContext.Provider value={value}>
      {children}
    </DiscountContext.Provider>
  );
};
